import React from 'react';
import {
    Container,
    Typography,
    Paper,
    Box,
    Divider,
    Grid,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import { styled } from '@mui/system';
import CheckIcon from '@mui/icons-material/Check';
import { Avatar, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import Tooltip from '@mui/material/Tooltip';


// Styled Components

const AvatarStyled = styled(Avatar)(({ theme }) => ({
    width: theme.spacing(14),
    height: theme.spacing(14),
    margin: '0 auto',
    marginBottom: theme.spacing(2),
}));

const LinkedInButton = styled(IconButton)(({ theme }) => ({
    color: theme.palette.primary.main,
    transition: 'color 0.3s',
    '&:hover': {
        color: theme.palette.primary.dark,
    },
}));


const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(6),
    marginTop: theme.spacing(6),
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[6],
    backgroundColor: theme.palette.background.paper,
}));

const Section = styled(Box)(({ theme }) => ({
    marginTop: theme.spacing(6),
}));

const BulletList = styled(List)(({ theme }) => ({
    paddingLeft: theme.spacing(4),
}));

// Sub-components
const CoreValues = () => (
    <Section>
        <Typography variant="h5" component="h2" gutterBottom>
            Our Core Values
        </Typography>
        <BulletList>
            <ListItem>
                <ListItemIcon>
                    <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                    primary={<strong>Champion Sustainability:</strong>}
                    secondary="Reduce waste, curb overconsumption, and support communities by offering solutions for short-term item needs."
                />
            </ListItem>
            <ListItem>
                <ListItemIcon>
                    <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                    primary={<strong>Enhance Convenience:</strong>}
                    secondary="Prioritize simplicity, accessibility, and affordability, making borrowing easier than buying."
                />
            </ListItem>
            <ListItem>
                <ListItemIcon>
                    <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                    primary={<strong>Promote Accessibility:</strong>}
                    secondary="Ensure experiences are available to all, so cost is never a barrier."
                />
            </ListItem>
            <ListItem>
                <ListItemIcon>
                    <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                    primary={<strong>Foster Trust:</strong>}
                    secondary="Rely on our secure, trusted platform for borrowing and lending with peace of mind."
                />
            </ListItem>
        </BulletList>
    </Section>
);

const JoinSection = () => {
    const theme = useTheme();
    return (
        <Section>
            <Typography variant="h5" component="h2" gutterBottom>
                Want to Join Our Team?
            </Typography>
            <Typography variant="body1" paragraph>
                If you're passionate about our mission and want to contribute to our growth, reach out through our{' '}
                <strong>Help Center</strong> and share how you can help us increase our impact.
            </Typography>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h5" component="h2" gutterBottom>
                Investor Questions
            </Typography>
            <Typography variant="body1" paragraph>
                If you share our vision and want to help Borrowed scale to new markets, send us a note at{' '}
                <a href="mailto:info@getborrowed.com" style={{ color: theme.palette.primary.main }}>
                    info@getborrowed.com
                </a>
            </Typography>
        </Section>
    );
};

const TeamSection = () => (
    <Section>
        <Typography variant="h5" component="h2" gutterBottom>
            Our Team
        </Typography>
        <Grid container spacing={6}>
            {/* Founder 1 */}
            <Grid item xs={12} md={4} textAlign="center">
                <AvatarStyled
                    src={`${process.env.PUBLIC_URL}/team/krystle.jpeg`} // Placeholder path
                    alt="Krystle Hawkesbury"
                />
                <Typography variant="h6">
                    <strong>Krystle Hawkesbury</strong>
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                    Co-founder & CEO
                </Typography>
                <Typography variant="body2" paragraph>
                    Krystle finds joy in bringing people together, nurturing their potential and leading teams to exceed expectations by achieving significant business outcomes. This ability has served Krystle well at Microsoft, where she was promoted seven-times in seven years, with her most recent promotion to Director of Cloud Solution Architecture. As an award-winning technology executive, Krystle brings a wealth of experience in sales leadership, people management and operational efficiency, which makes her well equipped to run the sales and operations functions of Borrowed. Krystle comes from a technology background, with her undergraduate in Information Technology, a Master of Science in Technology Management from Columbia University, and a current MBA Candidate for the Yale School of Management. Bringing the founders together, Krystle is excited to see Borrowed pioneer the sharing economy and help build a sustainable future.
                </Typography>
                {/* LinkedIn Icon */}
                <Tooltip title="Connect on LinkedIn">
                    <LinkedInButton
                        aria-label="Krystle Hawkesbury LinkedIn"
                        href="https://www.linkedin.com/in/krystlehawkesbury" // Replace with actual URL
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <LinkedInIcon />
                    </LinkedInButton>
                </Tooltip>
            </Grid>

            {/* Founder 2 */}
            <Grid item xs={12} md={4} textAlign="center">
                <AvatarStyled
                    src={`${process.env.PUBLIC_URL}/team/cella.jpeg`} // Placeholder path
                    alt="Marcella P. Arthur"
                />
                <Typography variant="h6">
                    <strong>Marcella P. Arthur</strong>
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                    Co-founder & CBO
                </Typography>
                <Typography variant="body2" paragraph>
                    Cella brings over twenty years of experience as a go-to-market strategist to Borrowed.  She played a pivotal role in helping both major tech companies and startups develop their sales and marketing strategies, streamline processes, and implement proven methodologies - contributing to the success of two IPOs and six exits. Her distinctive approach to growth-oriented waterfall planning has empowered organizations to leverage their existing teams in identifying business gaps, avenues for growth, and a more systematic path forward. Having held key leadership positions at Microsoft, Mimecast, Unbound Security, Webroot, and other security-focused tech companies, Cella understands the critical importance of a comprehensive end-to-end go-to-market strategy for scalable growth.

                </Typography>
                {/* LinkedIn Icon */}
                <Tooltip title="Connect on LinkedIn">
                    <LinkedInButton
                        aria-label="Marcella P. Arthur LinkedIn"
                        href="https://www.linkedin.com/in/marcellaarthur" // Replace with actual URL
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <LinkedInIcon />
                    </LinkedInButton>
                </Tooltip>
            </Grid>

            {/* Founder 3 */}
            <Grid item xs={12} md={4} textAlign="center">
                <AvatarStyled
                    src={`${process.env.PUBLIC_URL}/team/nacho.jpg`} // Placeholder path
                    alt="Ignacio Queralt"
                />
                <Typography variant="h6">
                    <strong>Ignacio Queralt</strong>
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                    Co-founder & CTO
                </Typography>
                <Typography variant="body2" paragraph>
                    Ignacio is a strategic technology leader with deep expertise in driving business growth and innovation through advancements in AI, machine learning, and cloud infrastructure. He has spearheaded the development of transformative AI-driven architectures, data collaboration and analytics platforms, and data-powered re-commerce solutions. At Booking.com, Ignacio led data-driven growth initiatives, underscoring his dedication to leveraging technology for meaningful results and significant growth. With a Master’s in Computer Science and a strong focus on emerging technologies, Ignacio is passionate about shaping the future. He is committed to advancing Borrowed’s vision by building a secure, scalable platform that empowers people to buy less, borrow more, and contribute to a more sustainable planet.
                </Typography>
                {/* LinkedIn Icon */}
                <Tooltip title="Connect on LinkedIn">
                    <LinkedInButton
                        aria-label="Ignacio Queralt LinkedIn"
                        href="https://www.linkedin.com/in/iqueralt"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        <LinkedInIcon />
                    </LinkedInButton>
                </Tooltip>
            </Grid>
        </Grid>
    </Section>
);

// Main Component
const AboutUs = () => (
    <Container maxWidth="lg" sx={{ mt: 8, mb: 8 }}>
        {/* Logo */}
        {/* <Box display="flex" justifyContent="center" alignItems="center" mb={6}>
                <LogoImage src={`${process.env.PUBLIC_URL}/logo_with_name.png`} alt="Borrowed Logo" />
            </Box> */}

        {/* Title */}
        <Typography variant="h3" component="h1" align="center" gutterBottom>
            About Us
        </Typography>

        {/* Content */}
        <StyledPaper>
            <Typography variant="body1" paragraph>
                At <strong>Borrowed</strong>, we make borrowing and lending everyday items simple and accessible. Our mission is to:
            </Typography>
            <BulletList>
                <ListItem>
                    <ListItemIcon>
                        <CheckIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Simplify the borrowing and lending process." />
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        <CheckIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Promote a sustainable sharing economy." />
                </ListItem>
                <ListItem>
                    <ListItemIcon>
                        <CheckIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary="Empower communities through accessible solutions." />
                </ListItem>
            </BulletList>

            <Typography variant="body1" paragraph>
                Founded by individuals passionate about reducing consumption and waste, Borrowed is for those who believe in
                the value of a sharing economy. We offer communities an alternative to buying by empowering people to borrow
                and lend with ease.
            </Typography>

            <Typography variant="body1" paragraph>
                We envision a world where people seamlessly connect within their communities to share everyday items. Borrowed
                creates new economic opportunities by enabling users to lend items they already own and keep <strong>100%</strong> of
                the listed price. Borrowers benefit as well, gaining access to items they need without the financial barriers of purchasing.
            </Typography>

            {/* Core Values */}
            <CoreValues />

            {/* Join Team & Investor Questions */}
            <JoinSection />

            {/* Team */}
            <TeamSection />
        </StyledPaper>
    </Container>
);

export default AboutUs;
